/**
 * Every way a session can fail, and the one sentence a player reads when it does.
 *
 * Netcode fails in ways nobody can act on ("ICE gathering timed out") unless something translates
 * them first, so the translation lives here rather than in each surface. A code is what the code
 * branches on, the sentence is what the lobby and the diagnostics panel both render, and neither
 * surface writes its own wording.
 *
 * Fatal means trying the next signaling strategy cannot help: a taken code stays taken on the
 * BitTorrent trackers too. Non-fatal means the failure is about reaching a relay, which the next
 * strategy might well manage.
 */

export type NetErrorCode =
  /** The pasted code is not a code: empty, wrong length, or a character not in the alphabet. */
  | 'invalidCode'
  /** Every registered signaling strategy was tried and none produced a live room. */
  | 'signalingExhausted'
  /** This strategy's relays did not answer inside the probe budget. */
  | 'relayUnreachable'
  /** Somebody else already hosts this code. Codes have no expiry, host presence is the expiry. */
  | 'codeAlreadyHosted'
  /** Nobody is hosting the code that was pasted. */
  | 'noHostForCode'
  /** The host has no free seat. Held seats count, so a reconnecting player keeps theirs. */
  | 'sessionFull'
  /** The peer speaks a protocol this build cannot read, or the other way round. */
  | 'versionMismatch'
  /** A guest met another guest. The star has one centre, so the pair is refused. */
  | 'notMyHost'
  /** A guest met a second host claiming the same code. */
  | 'duplicateHost'
  /** The host closed the session or dropped off the network. */
  | 'hostLeft'
  /** SDP was exchanged but no candidate pair ever worked, TURN included. */
  | 'peerConnectionFailed'
  /** A frame arrived that this build cannot parse. Never parsed halfway. */
  | 'malformedFrame'
  /** A snapshot delta named a baseline this client no longer holds. Recovered with a keyframe. */
  | 'staleBaseline'
  /** The transport was closed while something was waiting on it. */
  | 'sessionClosed'
  /**
   * Two peers generated different zones from the same recipe.
   *
   * Nothing a player did, and nothing a retry fixes: the generator is meant to be a pure function
   * of the recipe, so a disagreement is a bug in a build rather than a network fault. Loud at the
   * door beats silent in a firefight, which is the whole reason the hashes are exchanged.
   */
  | 'zoneMismatch';

interface NetErrorFact {
  readonly message: string;
  readonly fatal: boolean;
  /**
   * What to do about it, when there is something to do.
   *
   * The message says what happened and the advice says what would help, and they are separate
   * because the failures a player actually meets differ mostly in the second half: a typed code and
   * a full session both mean "you are not getting in", and only the advice tells them apart. Absent
   * where nothing useful can be said.
   */
  readonly advice?: string;
}

const NET_ERROR_FACTS: Record<NetErrorCode, NetErrorFact> = {
  invalidCode: {
    message: 'That is not a join code.',
    fatal: true,
    advice: 'Check it for a typo and type it again.',
  },
  signalingExhausted: {
    message: 'Could not reach the matchmaking relays.',
    fatal: true,
    advice: 'Try again in a moment, or from a different network.',
  },
  relayUnreachable: { message: 'These relays did not answer.', fatal: false },
  codeAlreadyHosted: {
    message: 'That code is already hosted.',
    fatal: true,
    advice: 'Host again to be given a new code.',
  },
  noHostForCode: {
    message: 'No one is hosting that code. The session may have ended.',
    fatal: false,
    advice: 'The host has to be sitting in the lobby before anyone can join.',
  },
  sessionFull: {
    message: 'That session is full.',
    fatal: true,
    advice: 'Four players is the limit, and a seat is kept briefly for anyone reconnecting.',
  },
  versionMismatch: {
    message: 'That player is running a different version of the game.',
    fatal: true,
    advice: 'Both of you reload the page to pick up the same version.',
  },
  notMyHost: { message: 'That player is not the host of this session.', fatal: false },
  duplicateHost: { message: 'Two players claim to host this session.', fatal: false },
  hostLeft: {
    message: 'The host left.',
    fatal: true,
    advice: 'Nobody takes over when the host leaves. Ask someone to host a new session.',
  },
  peerConnectionFailed: {
    message: 'Could not connect to the host.',
    fatal: true,
    advice: 'Your two networks could not reach each other. A different network often fixes it.',
  },
  malformedFrame: { message: 'A message arrived that this version cannot read.', fatal: false },
  staleBaseline: { message: 'Catching up with the host.', fatal: false },
  sessionClosed: { message: 'The session was closed.', fatal: true },
  zoneMismatch: {
    message: 'The players in this session built different zones.',
    fatal: true,
    advice: 'Everyone reload the page so the whole room is running the same version.',
  },
};

/**
 * A failure with a code the transport branches on and a sentence a surface renders as-is.
 *
 * `detail` carries the machine half: which relay, which character, which peer. It goes in the
 * diagnostics panel and the log, never in front of a player on its own.
 */
export class NetError extends Error {
  readonly code: NetErrorCode;
  readonly detail: string;
  readonly fatal: boolean;

  constructor(code: NetErrorCode, detail = '') {
    const fact = NET_ERROR_FACTS[code];
    super(detail === '' ? fact.message : `${fact.message} (${detail})`);
    this.name = 'NetError';
    this.code = code;
    this.detail = detail;
    this.fatal = fact.fatal;
  }
}

/** The player-facing sentence for a code, without the detail an error instance carries. */
export function netErrorMessage(code: NetErrorCode): string {
  return NET_ERROR_FACTS[code].message;
}

/** What a player can do about it, or null where nothing useful can be said. */
export function netErrorAdvice(code: NetErrorCode): string | null {
  return NET_ERROR_FACTS[code].advice ?? null;
}

/** True when no other signaling strategy could rescue this failure. */
export function isFatalNetError(code: NetErrorCode): boolean {
  return NET_ERROR_FACTS[code].fatal;
}

/** Narrows whatever a rejected promise carried. Anything else becomes a `sessionClosed` detail. */
export function asNetError(cause: unknown, fallback: NetErrorCode): NetError {
  if (cause instanceof NetError) return cause;
  return new NetError(fallback, cause instanceof Error ? cause.message : String(cause));
}
